// components/industry-templates/SportsEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, Flag, Clock, Users, MapPin, Award } from 'lucide-react';

export const SportsEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Race Categories */}
      {industryData?.race_categories && (
        <Card className="border-0 bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Flag className="w-8 h-8" style={{ color: theme.primary }} />
              Race Categories
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {industryData.race_categories.map((category: any, idx: number) => (
                <div
                  key={idx}
                  className="p-6 rounded-xl border-2 bg-white/5"
                  style={{ borderColor: theme.primary }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-1">{category.name}</h3>
                      <div className="flex gap-2">
                        <Badge style={{ backgroundColor: theme.primary }}>
                          {category.distance}
                        </Badge>
                        <Badge variant="outline" className="border-white text-white">
                          {category.age_group}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-3xl">🏃</div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Clock className="w-4 h-4" />
                      <span>Start: {category.start_time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Users className="w-4 h-4" />
                      <span>Max Participants: {category.max_participants}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Trophy className="w-4 h-4" />
                      <span>Prize: {category.prize}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Race Kit */}
      {industryData?.race_kit && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              🎒 Race Kit Includes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {industryData.race_kit.items.map((item: string, idx: number) => (
                <div
                  key={idx}
                  className="p-4 rounded-lg bg-white/5 border border-white/10 text-center"
                >
                  <div className="text-2xl mb-2">✓</div>
                  <p className="text-white font-medium text-sm">{item}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prizes */}
      {industryData?.prizes && (
        <Card
          className="border-2 backdrop-blur"
          style={{ borderColor: theme.primary, backgroundColor: 'rgba(22, 163, 74, 0.1)' }}
        >
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Trophy className="w-6 h-6" style={{ color: theme.primary }} />
              Prize Money
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(industryData.prizes).map(([position, prize]: [string, any], idx) => (
                <div
                  key={idx}
                  className="text-center p-6 rounded-lg bg-white/5 border border-white/10"
                >
                  <div className="text-4xl mb-2">
                    {position === '1st' ? '🥇' : position === '2nd' ? '🥈' : '🥉'}
                  </div>
                  <p className="text-slate-400 text-sm mb-1">{position} Place</p>
                  <p className="text-2xl font-bold" style={{ color: theme.primary }}>
                    {prize}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Route Map */}
      {industryData?.route_map && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MapPin className="w-6 h-6" style={{ color: theme.primary }} />
              Race Route
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-6 text-center">
              <MapPin className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <p className="text-white mb-2">Route Map Available</p>
              <p className="text-sm text-slate-400 mb-4">{industryData.route_map}</p>
              <Badge style={{ backgroundColor: theme.primary }} className="text-sm py-2 px-4">
                Download Route Map PDF
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
